import React from 'react'

export type PillButtonVariant =
  | 'solid'
  | 'primary'
  | 'ink'
  | 'dark'
  | 'pulse'
  | 'outline'
  | 'ghost'

export type PillButtonSize = 'sm' | 'md' | 'lg'

export interface PillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string
  variant?: PillButtonVariant
  size?: PillButtonSize
  badgeIcon?: string | null
  icon?: string | null
  loading?: boolean
  fullWidth?: boolean
  iconOnly?: boolean
  href?: string | null
}

const CSS = `
@keyframes wst-spin {
  to { transform: rotate(360deg); }
}

.wst-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2, 8px);
  font-family: var(--font-display, 'Unbounded', sans-serif);
  line-height: var(--lh-tight, 1.1);
  white-space: nowrap;
  text-decoration: none;
  border-radius: var(--radius-full, 9999px);
  border: 1.5px solid transparent;
  cursor: pointer;
  box-sizing: border-box;
  user-select: none;
  transition: transform var(--motion-fast, 150ms) var(--ease-standard, cubic-bezier(0.2, 0, 0, 1)),
              box-shadow var(--motion-fast, 150ms) var(--ease-standard, cubic-bezier(0.2, 0, 0, 1)),
              background-color var(--motion-fast, 150ms) var(--ease-standard, cubic-bezier(0.2, 0, 0, 1)),
              border-color var(--motion-fast, 150ms) var(--ease-standard, cubic-bezier(0.2, 0, 0, 1)),
              color var(--motion-fast, 150ms) var(--ease-standard, cubic-bezier(0.2, 0, 0, 1));
}

/* SIZES */
.wst-pill--sm {
  min-height: 36px;
  padding-left: var(--space-4, 16px);
  padding-right: var(--space-4, 16px);
  font-size: 13px;
  font-weight: var(--weight-semibold, 600);
}
.wst-pill--md {
  min-height: 46px;
  padding-left: var(--space-6, 24px);
  padding-right: var(--space-6, 24px);
  font-size: 14px;
  font-weight: var(--weight-semibold, 600);
}
.wst-pill--lg {
  min-height: 54px;
  padding-left: var(--space-8, 32px);
  padding-right: var(--space-8, 32px);
  font-size: 16px;
  font-weight: var(--weight-bold, 700);
  letter-spacing: -0.01em;
}
.wst-pill--full {
  width: 100%;
}
.wst-pill--icon-only {
  padding-left: 0;
  padding-right: 0;
  aspect-ratio: 1 / 1;
}

/* VARIANTS */
/* Solid Electric (Hero Primary CTA) */
.wst-pill--solid {
  background-color: var(--c-accent-electric, #38BFFF);
  color: var(--c-ink, #0B1230);
  box-shadow: 0 4px 14px -2px rgba(56, 191, 255, 0.45),
              inset 0 1px 1px 0 rgba(255, 255, 255, 0.45);
}
.wst-pill--solid:not(.wst-pill--disabled):hover {
  background-color: color-mix(in oklch, var(--c-accent-electric, #38BFFF) 92%, white);
  box-shadow: 0 6px 22px -2px rgba(56, 191, 255, 0.68),
              inset 0 1px 1px 0 rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
}

/* Solid Ink (High-contrast Dark CTA) */
.wst-pill--ink {
  background-color: var(--c-ink, #0B1230);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 14px -2px rgba(11, 18, 48, 0.28),
              inset 0 1px 1px 0 rgba(255, 255, 255, 0.15);
}
.wst-pill--ink:not(.wst-pill--disabled):hover {
  background-color: color-mix(in oklch, var(--c-ink, #0B1230) 88%, white);
  box-shadow: 0 6px 20px -2px rgba(11, 18, 48, 0.45);
  transform: translateY(-2px);
}

/* Pulse Energy Variant */
.wst-pill--pulse {
  background-color: var(--c-pulse, #2AE6B6);
  color: var(--c-ink, #0B1230);
  box-shadow: 0 4px 14px -2px rgba(42, 230, 182, 0.45),
              inset 0 1px 1px 0 rgba(255, 255, 255, 0.5);
}
.wst-pill--pulse:not(.wst-pill--disabled):hover {
  background-color: color-mix(in oklch, var(--c-pulse, #2AE6B6) 92%, white);
  box-shadow: 0 6px 22px -2px rgba(42, 230, 182, 0.7),
              inset 0 1px 1px 0 rgba(255, 255, 255, 0.7);
  transform: translateY(-2px);
}

/* Outline Secondary Variant */
.wst-pill--outline {
  background-color: transparent;
  color: var(--c-accent-electric, #38BFFF);
  border-color: rgba(56, 191, 255, 0.4);
}
.wst-pill--outline:not(.wst-pill--disabled):hover {
  background-color: rgba(56, 191, 255, 0.08);
  border-color: var(--c-accent-electric, #38BFFF);
  box-shadow: 0 4px 14px -2px rgba(56, 191, 255, 0.25);
  transform: translateY(-2px);
}

/* Ghost Tertiary Variant */
.wst-pill--ghost {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-color: transparent;
}
.wst-pill--ghost:not(.wst-pill--disabled):hover {
  background-color: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  transform: translateY(-2px);
}

/* GLOBAL INTERACTIONS & STATES */
.wst-pill:focus-visible {
  outline: 2px solid var(--focus-ring, #38BFFF);
  outline-offset: var(--focus-ring-offset, 2px);
}
.wst-pill:not(.wst-pill--disabled):active {
  transform: translateY(0);
}
.wst-pill--disabled {
  cursor: not-allowed;
  opacity: 0.45;
  box-shadow: none !important;
  transform: none !important;
}

/* BADGES & ICONS */
.wst-pill__icon {
  font-size: 1.1em;
  line-height: 1;
}
.wst-pill__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55em;
  height: 1.55em;
  font-size: 0.78em;
  border: 1px solid currentColor;
  border-radius: var(--radius-full, 9999px);
  margin-left: var(--space-1, 4px);
  transition: transform var(--motion-fast, 150ms) var(--ease-standard, ease);
}
.wst-pill:hover .wst-pill__badge {
  transform: translateX(2px);
}

/* SPINNER */
.wst-pill__spinner {
  display: inline-block;
  width: 1.1em;
  height: 1.1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: wst-spin 0.7s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .wst-pill, .wst-pill:hover, .wst-pill:focus-visible, .wst-pill:active, .wst-pill__badge {
    transition: none;
    transform: none;
  }
}
`

if (typeof document !== 'undefined' && !document.getElementById('wst-pill-css')) {
  const s = document.createElement('style')
  s.id = 'wst-pill-css'
  s.textContent = CSS
  document.head.appendChild(s)
}

export const PillButton: React.FC<PillButtonProps> = ({
  label,
  variant = 'solid',
  size = 'md',
  badgeIcon = null,
  icon = null,
  loading = false,
  fullWidth = false,
  iconOnly = false,
  disabled = false,
  href = null,
  type = 'button',
  children,
  className = '',
  ...rest
}) => {
  const isInk = variant === 'ink' || variant === 'dark'
  const isPulse = variant === 'pulse'
  const isOutline = variant === 'outline'
  const isGhost = variant === 'ghost'

  let variantCls = 'wst-pill--solid'
  if (isInk) variantCls = 'wst-pill--ink'
  else if (isPulse) variantCls = 'wst-pill--pulse'
  else if (isOutline) variantCls = 'wst-pill--outline'
  else if (isGhost) variantCls = 'wst-pill--ghost'

  const isDisabled = disabled || loading

  const cls = [
    'wst-pill',
    variantCls,
    `wst-pill--${size}`,
    fullWidth ? 'wst-pill--full' : '',
    iconOnly ? 'wst-pill--icon-only' : '',
    isDisabled ? 'wst-pill--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {loading && <span className="wst-pill__spinner" aria-hidden="true" />}
      {!loading && icon && <i className={`pi ${icon} wst-pill__icon`} aria-hidden="true" />}
      {!iconOnly && <span>{label ?? children}</span>}
      {iconOnly && !loading && (
        <i className={`pi ${icon || badgeIcon} wst-pill__icon`} aria-hidden="true" />
      )}
      {!loading && !iconOnly && badgeIcon && (
        <span className="wst-pill__badge" aria-hidden="true">
          <i className={`pi ${badgeIcon}`} />
        </span>
      )}
    </>
  )

  if (href && !isDisabled) {
    return (
      <a
        className={cls}
        href={href}
        role="button"
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    )
  }

  return (
    <button className={cls} type={type} disabled={isDisabled} aria-busy={loading} {...rest}>
      {content}
    </button>
  )
}
